import React, { useContext } from 'react'
import { PaperFoldBox } from '../Core/PaperFoldBox'
import { Button, Divider, Grid, useMediaQuery } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import VisibilityIcon from '@material-ui/icons/Visibility'
import FlexView from 'react-flexview/lib'
import { ReactComponent as Shield } from '../../assets/shield_with_v.svg'
import { ReactComponent as RedShield } from '../../assets/red_shield_with_v.svg'
import { ReactComponent as Hourglass } from '../../assets/Hourglass.svg'
import { ReactComponent as ShieldSuspend } from '../../assets/shield_suspended.svg'
import { ReactComponent as RenewalTimer } from '../../assets/renewal_timer.svg'
import { ReactComponent as Calendar } from '../../assets/Calendar.svg'
import { ReactComponent as BlackShiled } from '../../assets/black_shiled_with_arrow.svg'
import { ReactComponent as Download } from '../../assets/download.svg'
import './PolicyListCard.scss'
import { InsuranceStatus } from '../../models/insurance-models/InsuranceStatus'
import dayjs from 'dayjs'
import { BrowserView } from 'react-device-detect'
import { TextButton } from '../Core/Buttons/TextButton'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { awaitWithLoading } from '../../services/utils.js'
import { SessionService } from '../../services/SessionService'
import { CancelPolicySection } from './Sections/'
import { EventEmitterService, EventKey } from '../../services/EventEmitterService'
import { SessionContextStore } from '../../context/SessionContext'
import { ACTIONS, PolicyModificationContextStore } from '../../context/PolicyModificationContext'
import { InsuredMotorcyclesSection } from './Sections/InsuredMotorcyclesSection'
import { getPolicyDetails } from './PolicyModification/utils'
import insuranceApi from '../../api/insurance.api.js'
import PolicyEndorsements from './PolicyEndorsements.js'
import policyModificationApi from '../../api/policyModification.api.js'

let classNames = require('classnames')

export const PolicyListCard = ({ insurance, buttons, small, ...props }) => {
  let { url } = useRouteMatch()
  const history = useHistory()
  const modifyContext = useContext(PolicyModificationContextStore)
  const sessionContext = React.useContext(SessionContextStore)

  const [cancellations, setCancellations] = React.useState([])
  const [endorsements, setEndorsements] = React.useState([])

  const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'))

  const openModify = async () => {
    const res = await awaitWithLoading(getPolicyDetails(insurance.insuranceId))
    if (res.ok) {
      const vehicles = res.data.insured_vehicles.sort((a, b) => a.id - b.id)
      const vehicleMaxId = vehicles[vehicles.length - 1].id

      const riders = res.data.insured_person.sort(i => i.id)
      const riderMaxId = riders[riders.length - 1].id
      modifyContext.dispatch({
        type: ACTIONS.SET_POLICY_DATA,
        data: {
          insuranceId: insurance.insuranceId,
          owner_details: res.data.owner_details,
          motorcycles_details: res.data.insured_vehicles,
          rider_details: res.data.insured_person,
          policy_coverages: res.data.policy_coverages,
          billingPeriod: res.data.billing_period,
          isRenewal: res.data.is_renewal,
          riderMaxId: riderMaxId,
          vehicleMaxId: vehicleMaxId,
        },
      })
      history.push(`${url}/${insurance.insuranceId}/modify-policy`, { open_details: true })
    } else {
      EventEmitterService.dispatch(EventKey.ShowError, res)
    }
  }

  const openPolicyUrl = () => {
    window.open(insurance.policyUrl, '_blank')
  }

  const formatPolicyNumber = policyNumber => {
    return `VMT${policyNumber.padStart(11, '0')}`
  }

  const shieldClassName = classNames('policy-card-list-shield')

  const getShield = () => {
    switch (insurance.getStatus()) {
      case InsuranceStatus.ACTIVE:
        return <Shield className={shieldClassName} />
      case InsuranceStatus.CANCELED:
        return <RedShield className={shieldClassName} />
      case InsuranceStatus.SCHEDULED:
        return <Calendar className={shieldClassName} />
      case InsuranceStatus.PENDING:
        return <Hourglass className={classNames(shieldClassName, 'lightorange')} />
      case InsuranceStatus.SUSPENDED:
        return <ShieldSuspend className={classNames(shieldClassName, 'yellow-svg-path')} />
      case InsuranceStatus.RENEWAL:
        return <RenewalTimer className={shieldClassName} />
      case InsuranceStatus.EXPIRED:
        return <BlackShiled className={shieldClassName} />
      default:
        return <Shield className={shieldClassName} />
    }
  }

  const shieldLabelClassName = classNames('bold text-body margin-top-11')

  const getShieldLabel = () => {
    switch (insurance.getStatus()) {
      case InsuranceStatus.ACTIVE:
        return <p className={classNames(shieldLabelClassName, 'green')}>Active</p>
      case InsuranceStatus.CANCELED:
        return <p className={classNames(shieldLabelClassName, 'red')}>Canceled</p>
      case InsuranceStatus.SCHEDULED:
        return <p className={classNames(shieldLabelClassName, 'royalblue')}>Scheduled</p>
      case InsuranceStatus.PENDING:
        return <p className={classNames(shieldLabelClassName, 'lightorange')}>Pending</p>
      case InsuranceStatus.RENEWAL:
        return <p className={classNames(shieldLabelClassName, 'eggplantpurple')}>Renewal</p>
      case InsuranceStatus.EXPIRED:
        return <p className={classNames(shieldLabelClassName)}> Year {insurance.tenure + 1}</p>
      default:
        return 'Policy'
    }
  }

  const getLabelText = () => {
    switch (insurance.getStatus()) {
      case InsuranceStatus.CANCELED:
        return ''
      case InsuranceStatus.EXPIRED:
        return 'Out of date policy'
      case InsuranceStatus.RENEWAL:
        return 'The following policy will be applied soon'
      default:
        return 'Open activation email or text message with your phone to upload your odometer reading'
    }
  }

  const getLabel = () => {
    switch (insurance.getStatus()) {
      case InsuranceStatus.ACTIVE:
        return 'Active Policy'
      case InsuranceStatus.SCHEDULED:
        return 'Future Policy'
      case InsuranceStatus.PENDING:
        return !isMobile ? 'Pending Policy - Action Required to Activate Policy' : 'Pending Policy'
      case InsuranceStatus.EXPIRED:
        return 'Ended Policy'
      case InsuranceStatus.RENEWAL:
        return 'Renewal Policy'
      case InsuranceStatus.CANCELED:
        return 'Canceled Policy'
      default:
        return 'Policy'
    }
  }

  const getPolicyStatusColor = () => {
    return classNames(
      { 'green-style': insurance.getStatus() == InsuranceStatus.ACTIVE },
      { 'orange-style': insurance.getStatus() == InsuranceStatus.PENDING },
      { 'blue-style': insurance.getStatus() == InsuranceStatus.SCHEDULED },
      { 'red-style': insurance.getStatus() == InsuranceStatus.CANCELED },
      { 'black-style': insurance.getStatus() == InsuranceStatus.EXPIRED },
      { 'purple-style': insurance.getStatus() == InsuranceStatus.RENEWAL }
    )
  }

  const onPolicyDownload = async () => {
    const res = await awaitWithLoading(policyModificationApi.getPolicyOriginalPdf(insurance.insuranceId))
    window.open(res.data.policy_pdf_url, '_blank')
  }

  const onEndorsementDownload = async id => {
    const res = await awaitWithLoading(policyModificationApi.getEndorsement(insurance.insuranceId, id))
    window.open(res.data.midterm_pdf, '_blank')
  }

  React.useEffect(() => {
    if (SessionService.isAdmin()) {
      const setCancellationsState = async () => {
        const cancellations = await insuranceApi.getCancellations(insurance.insuranceId)

        setCancellations(
          cancellations.data.map(d => ({
            date: new Date(d.date),
            notice_url: d.notice_url,
            id: new Date(d.date).getTime(),
          }))
        )
      }
      setCancellationsState()
    }
  }, [])

  React.useEffect(() => {
    const setEndorsementsState = async () => {
      const endorsements = await awaitWithLoading(policyModificationApi.getMidterms(insurance.insuranceId))
      setEndorsements(
        endorsements.data.policy_midterms.map(e => ({
          url: e.midterm_pdf_url,
          id: e.id,
          effective_date: dayjs(e.effective_date).format('MM/DD/YYYY'),
        }))
      )
    }
    setEndorsementsState()
  }, [])

  return (
    <PaperFoldBox
      buttons={
        <FlexView className={classNames('space-evenly', { 'padding-bottom-basic': isMobile })}>
          {
            <React.Fragment>
              {isMobile && (
                <FlexView vAlignContent={'center'} column>
                  {insurance.hasPolicy() && (
                    <React.Fragment>
                      <TextButton
                        className="view-button-mobile"
                        onClick={openPolicyUrl}
                        text={'Policy'}
                        endIcon={<VisibilityIcon fontSize="small" className="blue" />}
                      />
                    </React.Fragment>
                  )}
                  {SessionService.isAdmin() && !insurance.isCanceledOrExpired() && (
                    <Grid
                      item
                      xs={10}
                      md={3}
                      class={'margin-top-small'}
                      style={{ display: 'inline-flex', justifyContent: 'flex-end', marginLeft: 'auto' }}
                    >
                      <CancelPolicySection insurance={insurance} />
                    </Grid>
                  )}
                </FlexView>
              )}
            </React.Fragment>
          }
        </FlexView>
      }
    >
      <div
        className={classNames(
          { 'policy-list-card-title policy-list-card-title-padding': !small },
          { 'policy-list-card-title-small': small }
        )}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} md={12}>
            <FlexView vAlignContent={'center'} className="space-between">
              <FlexView vAlignContent={'center'} className="space-between text-small black">
                {isMobile && getShield()}
                <div>
                  <FlexView vAlignContent="bottom">
                    <div className={classNames(isMobile ? 'title-style-mobile' : 'text-title', 'bold')}> {getLabel()} </div>
                    {SessionService.isAdmin() && insurance.estimatedMilesInsurance && (
                      <div className="margin-left-small">(New Logic - States Alignment)</div>
                    )}
                  </FlexView>

                  {!isMobile && <p className={classNames('text-small-title', '')}>{getLabelText()}</p>}
                  {!isMobile && insurance.roadsideUrl && insurance.roadsideUrl !== '' && (
                    <div>
                      <a href={insurance.roadsideUrl} target="_blank" rel="noreferrer">
                        VIEW ROADSIDE CERTIFICATE
                      </a>
                    </div>
                  )}
                </div>
              </FlexView>
              {insurance.isActiveOrSchedule() && (
                <BrowserView>
                  {!isMobile && (
                    <FlexView column hAlignContent="right" vAlignContent="top">
                      <TextButton
                        className="view-button view-button-margin"
                        onClick={openPolicyUrl}
                        endIcon={<VisibilityIcon fontSize="small" />}
                        text={'Policy'}
                      />
                      {/* {insurance.latestEndorsementUrl && insurance.latestEndorsementUrl !== "" && <TextButton className="view-button" onClick={openLatesetEndorsementUrl} endIcon={<VisibilityIcon fontSize="small" />} text={'Endorsed Policy'} />} */}
                      {SessionService.isAdmin() && insurance.isActive() && !insurance.isRenewal() && (
                        <TextButton
                          className="view-button"
                          onClick={openModify}
                          endIcon={<DescriptionIcon fontSize="small" />}
                          text={'Modify Policy'}
                        />
                      )}
                      {SessionService.isAdmin() && insurance.isRenewal() && (
                        <TextButton
                          className="view-button"
                          onClick={openModify}
                          endIcon={<DescriptionIcon fontSize="small" />}
                          text={'Modify Policy'}
                        />
                      )}
                    </FlexView>
                  )}
                </BrowserView>
              )}
            </FlexView>
          </Grid>
        </Grid>

        <Divider className="devider-style" />
      </div>

      <FlexView style={{ paddingRight: '20px', width: '100%' }}>
        <Grid container style={{ width: '100%' }} item xs={SessionService.isAdmin() && !isMobile ? 8 : 12}>
          {!isMobile && (
            <Grid item xs={2}>
              <FlexView column vAlignContent="center" hAlignContent="center">
                <div className="background_status_icon">
                  <FlexView column vAlignContent="center" hAlignContent="center" className="margin-top-basic">
                    {getShield()}
                    {getShieldLabel()}
                  </FlexView>
                </div>
              </FlexView>
            </Grid>
          )}
          <Grid item xs={isMobile ? 12 : 10}>
            <FlexView vAlignContent={'center'} column hAlignContent={'left'} className={!isMobile && 'margin-left-50'}>
              <div
                className={classNames(
                  getPolicyStatusColor(),
                  isMobile && 'text-small',
                  'policy-number-rectangle margin-top-small limited-text-200'
                )}
              >
                <span className="bold">Policy Number: </span>
                {formatPolicyNumber(insurance.policyNumber)}
              </div>
              <div className="bold margin-top-small text-small">Coverage Period</div>
              <div className="margin-top-3 text-body">
                {dayjs(insurance.validFromUtc).format('M/D/YYYY')} - {dayjs(insurance.validUntilUtc).format('M/D/YYYY')}
              </div>
              <InsuredMotorcyclesSection insurance={insurance} />
              {isMobile && insurance.roadsideUrl && insurance.roadsideUrl !== '' && (
                <div className="margin-top-small text-small">
                  <a href={insurance.roadsideUrl} target="_blank" rel="noreferrer">
                    VIEW ROADSIDE CERTIFICATE
                  </a>
                </div>
              )}
              {SessionService.isAdmin() && (
                <FlexView hAlignContent={'left'}>
                  {
                    <Button className={`bold underline validate-odometer-control-button margin-top-6`}>
                      <a
                        rel="noreferrer"
                        style={{ color: 'var(--blue)' }}
                        target="_blank"
                        href={`${process.env.REACT_APP_NEW_APP_DASHBOARD_URL}/redirect?url=/user/${sessionContext.user.userId}/odometers`}
                      >
                        {' '}
                        Go To Odometers
                      </a>
                    </Button>
                  }
                </FlexView>
              )}

              {!!cancellations.length && (
                <div className="cancel-wrapper">
                  <div className="cancel-title">Notice of Cancelation Forms</div>

                  {cancellations
                    .sort((a, b) => b.id - a.id)
                    .map((c, i) => (
                      <div key={c.id}>
                        <div className="cancel-item">
                          {' '}
                          <span className="cancel-item-text">
                            {' '}
                            #{i + 1} Document of Notice of Cancellation {dayjs(c.date).format('MM/DD/YYYY')}
                          </span>
                          <span>
                            <a className="cancel-item-button" rel="noreferrer" href={c.notice_url} target="_blank">
                              {' '}
                              <Download />
                            </a>
                          </span>
                        </div>{' '}
                        {i != cancellations.length - 1 && <Divider className="cancel-divider" />}
                      </div>
                    ))}
                </div>
              )}
              {endorsements.length > 0 && (
                <PolicyEndorsements
                  endorsements={endorsements}
                  originalPolicy={{
                    valid_from: dayjs(insurance.validFromUtc).format('MM/DD/YYYY'),
                    valid_to: dayjs(endorsements[0].effective_date).format('MM/DD/YYYY'),
                  }}
                  onPolicyDownload={onPolicyDownload}
                  onEndorsementDownload={onEndorsementDownload}
                />
              )}
            </FlexView>
          </Grid>
        </Grid>
        {SessionService.isAdmin() && !insurance.isCanceledOrExpired() && !isMobile && (
          <Grid item xs={10} md={3} style={{ display: 'inline-flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
            <CancelPolicySection insurance={insurance} />
          </Grid>
        )}
      </FlexView>
    </PaperFoldBox>
  )
}
